/**
 * Required-field validation for the authorisation details review screen.
 *
 * The six fields (auth_number, source_portal, num_visits, start_date,
 * end_date, auth_status) must hold valid values before Save is allowed.
 * Invalid values: null/undefined, empty/whitespace-only, the "—" display
 * placeholder, "-", "0", and "NA"/"N/A" (case-insensitive). `num_visits`
 * must additionally be a positive whole number because the backend coerces
 * it with `int()` on save.
 *
 * The check is approval-agnostic by design: it inspects only field values
 * (UI field state, falling back to the original `auth_letter` values the
 * screen displays), never the section Approve/Reject chips.
 *
 * @module authorisationDetails/utils/validateRequiredAuthFields
 */
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import {
  BASIC_INFO_FIELD_CONFIGS,
  NUM_VISITS_FIELD_KEY,
  PROCEDURE_FIELD_CONFIGS,
  REQUIRED_BASIC_INFO_FIELDS,
  REQUIRED_PROCEDURE_FIELDS,
  SOURCE_PORTAL_LABEL,
  type BasicInfoFieldState,
  type ProcedureFieldState,
} from "./constants";
import {
  getOriginalBasicInfoState,
  getOriginalFieldState,
} from "./saveReviewHelpers";

/** Human label lookup for the required basic-info fields (from the config, plus the standalone Source Portal). */
const BASIC_INFO_LABELS = {
  ...Object.fromEntries(BASIC_INFO_FIELD_CONFIGS.map((c) => [c.key, c.label])),
  sourcePortal: SOURCE_PORTAL_LABEL,
} as Record<keyof BasicInfoFieldState, string>;

/**
 * Field value kinds accepted by {@link isInvalidRequiredAuthValue}.
 * "count" fields (num_visits) must additionally be a positive whole number.
 */
export type RequiredFieldKind = "text" | "count";

/** Sentinel values that never count as a real value, compared after trim + lowercasing. "-" is a common "no value" marker in auth letters. */
const INVALID_SENTINEL_VALUES = new Set(["na", "n/a", "-"]);

/**
 * Determines whether a required auth-details field holds an invalid value.
 *
 * @param value - Raw value (UI string, or the original auth-letter value)
 * @param kind - "text" for free-text fields, "count" for num_visits
 * @returns True when the value blocks Save
 */
export function isInvalidRequiredAuthValue(
  value: string | null | undefined,
  kind: RequiredFieldKind = "text",
): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "—") return true;
  if (INVALID_SENTINEL_VALUES.has(trimmed.toLowerCase())) return true;
  if (trimmed === "0") return true;
  if (kind === "count") {
    return !/^\d+$/.test(trimmed) || Number(trimmed) < 1;
  }
  return false;
}

/** One required field holding an invalid value. */
export interface RequiredAuthFieldIssue {
  /** Which section the field belongs to. */
  section: "basicInformation" | "procedure";
  /** J-code row id (`id ?? j_code`); undefined for order-level fields. */
  jCodeId?: string;
  /** J-code label for display, e.g. "J1745" (procedure rows only). */
  jCodeLabel?: string;
  /** UI state key, e.g. "authorizationId" | "sourcePortal" | "numVisits". */
  fieldKey: string;
  /** Human label, e.g. "Authorization ID", "Num Visits". */
  fieldLabel: string;
  /** Short human reason, e.g. 'has invalid value "NA"'. */
  reason: string;
}

/** Result of {@link validateRequiredAuthFields}. */
export interface RequiredAuthFieldsCheckResult {
  /** True when every required field holds a valid value. */
  valid: boolean;
  /** One issue per invalid required field. */
  issues: RequiredAuthFieldIssue[];
}

/** Builds a human reason for an invalid raw value (echoes short values). */
function reasonFor(value: string | null | undefined): string {
  if (value == null || value.trim() === "" || value.trim() === "—") {
    return "is missing or blank";
  }
  const trimmed = value.trim();
  if (INVALID_SENTINEL_VALUES.has(trimmed.toLowerCase()) || trimmed === "0") {
    return `has invalid value "${trimmed}"`;
  }
  // Only reachable for count fields (non-numeric or zero visit counts)
  return "must be a positive whole number";
}

/**
 * Validates all required auth-details fields.
 *
 * Order-level (checked once): `authorizationId`, `sourcePortal` — taken from
 * the first J-code's `auth_letter`. Per-J-code (checked for EVERY row):
 * `numVisits`, `startDate`, `endDate`, `authStatus`.
 *
 * @param params.jCodes - J-code rows from auth-on-file
 * @param params.basicInfoFieldState - Current basic-info UI state (may be null before first render)
 * @param params.procedureFieldStateMap - Current per-J-code procedure UI state
 * @returns `valid` flag plus one issue per invalid required field
 */
export function validateRequiredAuthFields(params: {
  jCodes: JCodeDetails[];
  basicInfoFieldState?: BasicInfoFieldState | null;
  procedureFieldStateMap?: Record<string, ProcedureFieldState> | null;
}): RequiredAuthFieldsCheckResult {
  const { jCodes, basicInfoFieldState, procedureFieldStateMap } = params;
  if (jCodes.length === 0) {
    return { valid: true, issues: [] };
  }

  const issues: RequiredAuthFieldIssue[] = [];

  // Order-level fields (checked once): effective value = UI state when
  // present, else the original auth_letter value the screen displays.
  const firstJCode = jCodes[0];
  const originalBasic = getOriginalBasicInfoState(firstJCode);
  for (const key of REQUIRED_BASIC_INFO_FIELDS) {
    const value = basicInfoFieldState?.[key] ?? originalBasic[key];
    if (isInvalidRequiredAuthValue(value, "text")) {
      issues.push({
        section: "basicInformation",
        fieldKey: key,
        fieldLabel: BASIC_INFO_LABELS[key],
        reason: reasonFor(value),
      });
    }
  }

  // Per-J-code fields: every row must be valid.
  for (const j of jCodes) {
    const id = j.id ?? j.j_code ?? "";
    const jCodeLabel = (j.j_code ?? "").trim().toUpperCase();
    const state = procedureFieldStateMap?.[id] ?? getOriginalFieldState(j);
    for (const key of REQUIRED_PROCEDURE_FIELDS) {
      const config = PROCEDURE_FIELD_CONFIGS.find((c) => c.key === key);
      const value = state[key];
      if (
        isInvalidRequiredAuthValue(
          value,
          key === NUM_VISITS_FIELD_KEY ? "count" : "text",
        )
      ) {
        issues.push({
          section: "procedure",
          jCodeId: id,
          jCodeLabel,
          fieldKey: key,
          fieldLabel: config?.label ?? key,
          reason: reasonFor(value),
        });
      }
    }
  }

  return { valid: issues.length === 0, issues };
}
